import { CommonModule } from '@angular/common';
import { Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { CheckboxModule } from 'primeng/checkbox';
import { DropdownModule } from 'primeng/dropdown';
import { MultiSelectModule } from 'primeng/multiselect';
import { TableModule } from 'primeng/table';
import { CalendarModule } from 'primeng/calendar';
import { RelatedDeclarationModalComponent } from '../related-declaration-modal/related-declaration-modal.component';
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome';
import { faChevronDown, faCircle, faCircleCheck, faClockRotateLeft, faFilePlus, faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { PresentationTypeEnum } from '../../core/enums/presentation-type.enum';
import { TypeDeclarationEnum } from '../../core/enums/type-declaration.enum';
import { Router } from '@angular/router';
import { SortEvent } from 'primeng/api';
import { DEPARTMENTS_TYPES } from '../../core/constants/client.constants';
import _ from 'lodash';
import { IbanFormatPipe } from "../../core/pipes/iban-format.pipe";
import { faArrowDownToLine } from '@fortawesome/pro-light-svg-icons';
import { FilesService } from '../../core/services/files.service';
import { DeclarationAvisosModalComponent } from '../declaration-avisos-modal/declaration-avisos-modal.component';

export interface HistoricTableFilters {
  models?: number[];
  clients?: number[];
  clientCodes?: number[];
  presentationTypes?: string[];
  status?: string[];
  hasNotifications?: boolean | null;
  hasComments?: boolean | null;
  responsibles?: number[];
  executors?: number[];
  selectedPeriod?: Date[];
  relatedDeclarationType?: string | null;
  year?: number | null;
}

@Component({
  selector: 'app-prime-table',
  standalone: true,
  imports: [TableModule, CommonModule, MultiSelectModule, FormsModule, DropdownModule, CheckboxModule, FontAwesomeModule, IbanFormatPipe, DeclarationAvisosModalComponent, CalendarModule, RelatedDeclarationModalComponent],
  templateUrl: './prime-table.component.html',
  styleUrl: './prime-table.component.scss'
})
export class PrimeTableComponent implements OnChanges {
  @Input() configTable: any;
  @Output() declarationChange: EventEmitter<number> = new EventEmitter<number>();
  @Output() serverFilterChange = new EventEmitter<HistoricTableFilters>();
  @Output() createAsociedDeclaration = new EventEmitter<any>();
  @ViewChild('newDecModal') newDecModal: RelatedDeclarationModalComponent;
  @ViewChild('avisosModal') avisosModal: DeclarationAvisosModalComponent;

  declarations: any;
  filteredDeclarations: any;
  selectedModels: any = [];
  selectedClients: any = [];
  selectedFase: any;
  selectedPresentationTypes: any;
  selectedResponsible: any;
  selectedExecutor: any;
  selectedClientCodes: any = [];
  selectedApplicationCodes: any;
  selectedApplications: any;

  originalModels: any = [];

  selectedHash: any = undefined;
  showRelatedDecModal: boolean = false;
  order = -1;
  sortField: string = 'model';
  sortOrder: number = 1;
  isRestoringSort: boolean = false;
  faCircleCheck = faCircleCheck;
  faCircle = faCircle;
  faChevronDown = faChevronDown;
  faFilePlus = faFilePlus;
  faClockRotateLeft = faClockRotateLeft;
  faDownload = faArrowDownToLine;
  faTriangleExclamation = faTriangleExclamation;

  PresentationTypeEnum = PresentationTypeEnum;
  TypeDeclarationEnum = TypeDeclarationEnum;
  departmentsTypes = DEPARTMENTS_TYPES;
  clientCodes: any = [];
  applicationCodes: any = [];
  applications: any = [];

  yesNoOptions = [
    { label: 'Sí', value: true },
    { label: 'No', value: false },
  ];

  declarationTypes = Object.keys(TypeDeclarationEnum).map(type => ({
    label: TypeDeclarationEnum[type],
    value: type,
  }));

  selectedId: any = undefined;

  get isServerSide(): boolean {
    return !!this.configTable?.serverSide;
  }

  get activeFilters(): HistoricTableFilters {
    return this.configTable?.activeFilters ?? {};
  }

  filters: any = {
    model: {
      value: this.selectedModels,
      filter: (obj: any): boolean => {
        return this.selectedModels.contains(obj.model.code);
      }
    },
  };
  selectedDecsLength: any = 0;

  constructor(
    private router: Router,
    private filesService: FilesService,
  ) { }

  ngOnInit() {
    if (this.isServerSide) {
      this.initServerSideData();
      return;
    }

    const savedFilters = localStorage.getItem('primeTableFilters');
    if (savedFilters) {
      const filters = JSON.parse(savedFilters);
      this.selectedModels = filters.selectedModels || undefined;
      this.selectedClients = filters.selectedClients || undefined;
      this.selectedFase = filters.selectedFase || undefined;
      this.selectedPresentationTypes = filters.selectedPresentationTypes || undefined;
      this.selectedResponsible = filters.selectedResponsible || undefined;
      this.selectedExecutor = filters.selectedExecutor || undefined;
      this.selectedClientCodes = filters.selectedClientCodes || undefined;
      this.selectedApplicationCodes = filters.selectedApplicationCodes || [];
      this.selectedApplications = filters.selectedApplications || [];
      this.selectedApplicationCodes = filters.selectedApplicationCodes || [];
      // Restaurar ordenación guardada
      if (filters.sortField) {
        this.sortField = filters.sortField;
      }
      if (filters.sortOrder !== undefined) {
        this.sortOrder = filters.sortOrder;
      }
    }

    if (this.configTable.filtersData) {
      this.configTable.filtersData.clients = this.configTable.filtersData.clients.sort((a, b) => a.name.localeCompare(b.name));
      this.clientCodes = this.configTable.filtersData.clients.filter((client) => client.code).sort((a, b) => a.code - b.code);
      this.applicationCodes = this.configTable.filtersData.applicationCodes.sort((a, b) => a - b);
      this.applications = this.configTable.filtersData.applications.sort((a, b) => a.localeCompare(b));
      this.configTable.filtersData.users = this.configTable.filtersData.users.sort((a, b) => a.name.localeCompare(b.name));
      this.configTable.filtersData.originalUsers = [...this.configTable.filtersData.users];
    }
    if (this.configTable && this.configTable.data) {
      this.filteredDeclarations = this.configTable.data.declarations;
      this.declarations = [...this.configTable.data.declarations];
      
      // Inicializar originalModels basándose en los modelos presentes en la tabla
      if(this.configTable.filtersData && this.configTable.filtersData.models && this.declarations.length > 0) {
        const visibleModelNames = _.uniq(_.map(this.declarations, 'model.name'));
        this.originalModels = _.map(this.configTable.filtersData.models, department => ({
          ...department,
          models: _.filter(department.models, model => visibleModelNames.includes(model.name)),
        })).filter(department => department.models.length > 0);
        // Inicializar también configTable.filtersData.models con los modelos de la tabla
        this.configTable.filtersData.models = this.originalModels;
      } else if(this.configTable.filtersData && this.configTable.filtersData.models) {
        this.originalModels = this.configTable.filtersData.models;
      }
      
      this.applyCustomFilter();
      this.modifyFiltersContent('');
      // Aplicar ordenación guardada después de cargar los datos
      this.applySavedSort();
    } else if(this.configTable.filtersData && this.configTable.filtersData.models) {
      this.originalModels = this.configTable.filtersData.models;
    }

  }

  ngOnChanges(changes: SimpleChanges) {
    if (this.isServerSide) {
      if (changes['configTable']?.currentValue?.data?.declarations) {
        this.initServerSideData();
      }
      return;
    }

    // Si los datos cambian después de la inicialización (no es la primera vez), procesarlos y aplicar la ordenación guardada
    if (changes['configTable'] && changes['configTable'].currentValue && changes['configTable'].previousValue) {
      const currentConfig = changes['configTable'].currentValue;
      const previousConfig = changes['configTable'].previousValue;
      
      // Solo procesar si los datos realmente han cambiado
      if (currentConfig.data && currentConfig.data.declarations && 
          (!previousConfig.data || previousConfig.data.declarations !== currentConfig.data.declarations)) {
        // Procesar los datos de la misma manera que en ngOnInit
        this.filteredDeclarations = currentConfig.data.declarations;
        this.declarations = [...currentConfig.data.declarations];
        
        // Inicializar originalModels si es necesario
        if(currentConfig.filtersData && currentConfig.filtersData.models && this.declarations.length > 0) {
          const visibleModelNames = _.uniq(_.map(this.declarations, 'model.name'));
          this.originalModels = _.map(currentConfig.filtersData.models, department => ({
            ...department,
            models: _.filter(department.models, model => visibleModelNames.includes(model.name)),
          })).filter(department => department.models.length > 0);
          currentConfig.filtersData.models = this.originalModels;
        }
        
        // Aplicar filtros y ordenación
        this.applyCustomFilter();
        // Esperar un tick para asegurar que los datos estén procesados antes de ordenar
        setTimeout(() => {
          if (this.filteredDeclarations && this.filteredDeclarations.length > 0) {
            this.applySavedSort();
          }
        }, 0);
      }
    }
  }

  initServerSideData(): void {
    if (this.configTable?.filtersData?.clients) {
      this.clientCodes = this.configTable.filtersData.clients
        .filter((client: any) => client.code)
        .sort((a: any, b: any) => a.code - b.code);
    }
    if (this.configTable?.data?.declarations) {
      this.filteredDeclarations = this.configTable.data.declarations;
      this.declarations = [...this.configTable.data.declarations];
      this.declarations.forEach((item: any) => {
        if (item.isExpanded === undefined) {
          item.isExpanded = false;
        }
      });
    }
  }

  emitServerFilterChange(partial: Partial<HistoricTableFilters>): void {
    this.serverFilterChange.emit({ ...this.activeFilters, ...partial });
  }

  onHistoricModelChange(event: any): void {
    this.emitServerFilterChange({ models: event?.value ?? [] });
  }

  onHistoricClientChange(event: any): void {
    this.emitServerFilterChange({ clients: event?.value ?? [] });
  }

  onHistoricClientCodeChange(event: any): void {
    this.emitServerFilterChange({ clientCodes: event?.value ?? [] });
  }

  onHistoricPresentationChange(event: any): void {
    this.emitServerFilterChange({ presentationTypes: event?.value ?? [] });
  }

  onHistoricStatusChange(event: any): void {
    this.emitServerFilterChange({ status: event?.value ?? [] });
  }

  onHistoricHasNotificationsChange(event: any): void {
    this.emitServerFilterChange({ hasNotifications: event?.value ?? null });
  }

  onHistoricHasCommentsChange(event: any): void {
    this.emitServerFilterChange({ hasComments: event?.value ?? null });
  }

  onHistoricResponsibleChange(event: any): void {
    this.emitServerFilterChange({ responsibles: event?.value ?? [] });
  }

  onHistoricExecutorChange(event: any): void {
    this.emitServerFilterChange({ executors: event?.value ?? [] });
  }

  onHistoricPeriodChange(dates: Date[]): void {
    this.emitServerFilterChange({ selectedPeriod: dates ?? [] });
  }

  onHistoricRelatedDeclarationChange(event: any): void {
    this.emitServerFilterChange({ relatedDeclarationType: event?.value ?? null });
  }

  onHistoricYearChange(event: any): void {
    this.emitServerFilterChange({ year: event?.value ?? null });
  }

  toggleExpanded(item: any, event: Event): void {
    event.stopPropagation();
    item.isExpanded = !item.isExpanded;
  }

  openRelatedDecModal(id: any): void {
    this.selectedId = id;
    this.showRelatedDecModal = true;
  }

  closeRelatedDecModal(): void {
    this.showRelatedDecModal = false;
  }

  createRelatedDeclaration(event: string): void {
    const type = this.declarationTypes.find(t => t.label === event)?.value;
    this.createAsociedDeclaration.emit([this.selectedId, type]);
    this.showRelatedDecModal = false;
  }

  onClientSelected(event) {
    if(!event) {
      this.selectedClients = [];
    } else {
      this.selectedClients = event.value;

    }
    this.applyCustomFilter()
    
    this.modifyFiltersContent('client');
  }

  onClientCodeSelected(event) {
    if(!event) {
      this.selectedClientCodes = [];
    } else {
      this.selectedClientCodes = event.value;
      
    }
    this.applyCustomFilter()
    this.modifyFiltersContent('clientCode');
    // También actualizar el filtro de código de aplicación
    this.modifyFiltersContent('codeApplication');
  }

  onApplicationCodeSelected(event) {
    if(!event) {
      this.selectedApplicationCodes = [];
    } else {
      this.selectedApplicationCodes = event.value;
      
    }
    this.applyCustomFilter()
    this.modifyFiltersContent('applicationCode');
    // También actualizar el filtro de código de cliente
    this.modifyFiltersContent('clientCode');
  }

  onApplicationSelected(event) {
    if(!event) {
      this.selectedApplications = [];
    } else {
      this.selectedApplications = event.value;
      
    }
    this.applyCustomFilter()
    this.modifyFiltersContent('application');
  }

  onPhaseChange(event) {
    if(!event) {
      this.selectedFase = null;
    } else {
      this.selectedFase = event.value;
    }
    this.applyCustomFilter()
    this.modifyFiltersContent('phase');
  }

  onModelChange(event) {
    if(!event) {
      this.selectedModels = [];
    } else {
      this.selectedModels = event.value;
    }
    this.applyCustomFilter()
    this.modifyFiltersContent('model');

  }

  onPresentationTypesSelected(event) {
    this.selectedPresentationTypes = event.value;
    this.applyCustomFilter()
    this.modifyFiltersContent('presentationType');
  }

  onResponsibleSelected(event) {
    this.selectedResponsible = event.value;
    this.applyCustomFilter()
    this.modifyFiltersContent('responsible');
  }

  onExecutorSelected(event) {
    this.selectedExecutor = event.value;
    this.applyCustomFilter()
    this.modifyFiltersContent('executor');
  }

  modifyFiltersContent(filter) {
    const visibleData = {
      models: _.map(this.filteredDeclarations, 'model.name'),
      clients: _.map(this.filteredDeclarations, 'client'),
      clientCodes: _.map(this.filteredDeclarations, 'client.code'),
      presentationTypes: _.map(this.filteredDeclarations, 'presentationType'),
      responsibles: _.map(this.filteredDeclarations, 'responsible').filter(Boolean),
      executors: _.map(this.filteredDeclarations, 'executor').filter(Boolean),
      maxPhases: _.maxBy(this.filteredDeclarations, item => item.tasks.length)?.tasks.length || 0,
      applications: _.map(this.filteredDeclarations, 'application.name') || [],
      applicationCodes: _.map(this.filteredDeclarations, 'application.code') || []
    };

    // Helper function to check if any filters are active
    const hasActiveFilters = () => {
      return this.selectedModels.length > 0 || 
             this.selectedClients.length > 0 || 
             this.selectedClientCodes.length > 0 || 
             this.selectedApplicationCodes?.length > 0 || 
             this.selectedApplications?.length > 0 || 
             this.selectedResponsible || 
             this.selectedExecutor || 
             this.selectedFase !== undefined || 
             this.selectedPresentationTypes;
    };
    
  
    if (filter !== 'model' || this.selectedModels.length === 0) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        this.configTable.filtersData.models = _.map(this.originalModels, department => ({
          ...department,
          models: _.filter(department.models, model => visibleData.models.includes(model.name)),
        }));
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        this.configTable.filtersData.models = this.originalModels;
      }
    }
  
    if (filter !== 'client' || this.selectedClients.length === 0) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        this.configTable.filtersData.clients = _.chain(visibleData.clients)
          .uniqBy('code')
          .sortBy('name')
          .value();
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        this.configTable.filtersData.clients = _.chain(this.declarations)
          .map('client')
          .uniqBy('code')
          .sortBy('name')
          .value();
      }
    }

    if ((filter !== 'clientCode' && filter !== 'codeApplication' && filter !== 'applicationCode') || this.selectedClientCodes.length === 0 || this.clientCodes.length === 0) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        const codes = _.chain(this.filteredDeclarations)
          .map('client.code')
          .filter(code => code != null)
          .uniq()
          .sort((a, b) => a - b)
          .value();
        this.clientCodes = codes.map(code => ({ code }));
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        const codes = _.chain(this.declarations)
          .map('client.code')
          .filter(code => code != null)
          .uniq()
          .sort((a, b) => a - b)
          .value();
        this.clientCodes = codes.map(code => ({ code }));
      }
    }
  
    if (filter !== 'presentationType' || this.selectedPresentationTypes == null) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        this.configTable.filtersData.presentationTypes = _.uniq(visibleData.presentationTypes);
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        this.configTable.filtersData.presentationTypes = _.uniq(_.map(this.declarations, 'presentationType'));
      }
    }

    if (filter !== 'application' || this.selectedApplications == null) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        this.configTable.filtersData.applications = _.uniq(visibleData.applications);
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        this.configTable.filtersData.applications = _.uniq(_.map(this.declarations, 'application.name'));
      }
    }

    if ((filter !== 'codeApplication' && filter !== 'applicationCode' && filter !== 'clientCode') || this.selectedApplicationCodes == null || this.applicationCodes.length === 0) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        this.applicationCodes = _.chain(this.filteredDeclarations)
          .map('application.code')
          .filter(code => code != null)
          .uniq()
          .sort((a, b) => a - b)
          .value();
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        this.applicationCodes = _.chain(this.declarations)
          .map('application.code')
          .filter(code => code != null)
          .uniq()
          .sort((a, b) => a - b)
          .value();
      }
    }
  
    if (filter !== 'responsible' || this.selectedResponsible == null) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        const visibleResponsibles = _.chain(visibleData.responsibles)
          .uniqBy('name')
          .sortBy('name')
          .value();
        
        this.configTable.filtersData.users = [
          { name: 'Sin asignar', id: 'unassigned' },
          ...visibleResponsibles
        ];
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        const allResponsibles = _.chain(this.declarations)
          .map('responsible')
          .filter(Boolean)
          .uniqBy('name')
          .sortBy('name')
          .value();
        
        this.configTable.filtersData.users = [
          { name: 'Sin asignar', id: 'unassigned' },
          ...allResponsibles
        ];
      }
    }
  
    if (filter !== 'executor' || this.selectedExecutor == null) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        const visibleExecutors = _.chain(visibleData.executors)
          .uniq()
          .sort()
          .value();
        
        this.configTable.filtersData.executors = [
          { name: 'Sin asignar', id: null },
          ...visibleExecutors.map(executor => ({ name: executor, id: executor }))
        ];
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        const allExecutors = _.chain(this.declarations)
          .map('executor')
          .filter(Boolean)
          .uniq()
          .sort()
          .value();
        
        this.configTable.filtersData.executors = [
          { name: 'Sin asignar', id: null },
          ...allExecutors.map(executor => ({ name: executor, id: executor }))
        ];
      }
    }
  
    if (filter !== 'phase' || this.selectedFase == null) {
      if (hasActiveFilters()) {
        // Si hay filtros activos, usar solo los datos filtrados
        this.configTable.filtersData.fases = _.times(visibleData.maxPhases, i => ({
          name: `Fase ${i + 1}`,
          id: i + 1,
          selected: false,
        }));
      } else {
        // Si no hay filtros activos, usar todos los datos originales
        const maxPhases = _.maxBy(this.declarations, item => item.tasks.length)?.tasks.length || 0;
        this.configTable.filtersData.fases = _.times(maxPhases, i => ({
          name: `Fase ${i + 1}`,
          id: i + 1,
          selected: false,
        }));
      }
    }
    this.configTable.filtersData.presentationTypes = this.configTable.filtersData.presentationTypes?.filter(opt => opt != null);

  }
  
  

  applyCustomFilter() {
    this.filteredDeclarations = this.declarations?.filter(declaration => {
      const modelMatch = this.selectedModels.length > 0
        ? this.selectedModels.includes(declaration.model.name)
        : true;
  
      const clientMatch = this.selectedClients.length > 0
        ? this.selectedClients.includes(declaration.client.name)
        : true;
  
      const clientCodeMatch = this.selectedClientCodes.length > 0
        ? this.selectedClientCodes.includes(declaration.client.code)
        : true;

      const applicationCodeMatch = this.selectedApplicationCodes?.length > 0
      ? this.selectedApplicationCodes.includes(declaration.application.code)
      : true;

      const presentationMatch = this.selectedPresentationTypes
        ? this.selectedPresentationTypes.includes(declaration.presentationType)
        : true;
  
      const responsibleMatch = this.selectedResponsible
        ? this.selectedResponsible.id === 'unassigned'
          ? !declaration.responsible?.name
          : this.selectedResponsible.name === declaration.responsible?.name
        : true;
  
      const executorMatch = this.selectedExecutor
        ? this.selectedExecutor === 'Sin asignar'
          ? !declaration.executor
          : this.selectedExecutor === declaration.executor
        : true;

      const phaseMatch = this.selectedFase !== undefined
      ? this.checkPhaseConditions(declaration.tasks, this.selectedFase)
      : true;
      
      const applicationMatch = this.selectedApplications?.length > 0
      ? this.selectedApplications.includes(declaration.application.name)
      : true;

      return modelMatch && clientMatch && clientCodeMatch && applicationCodeMatch && presentationMatch && responsibleMatch && executorMatch && phaseMatch && applicationMatch;
    });

    this.saveFilters();
  }
  
  
  checkPhaseConditions(tasks: string[], phaseIndex: number): boolean {
    if (phaseIndex === 1) {
      const currentPhase = tasks[phaseIndex - 1];
      return currentPhase === 'PENDING' || currentPhase === undefined || currentPhase === null || currentPhase === 'STARTED' || currentPhase === 'NRC_RECEIVED' || currentPhase === 'CARATULA_NRC' || currentPhase === 'BLOCKED';
    } else {
      const currentPhase = tasks[phaseIndex - 1];
      const previousPhase = tasks[phaseIndex - 2];
      if (currentPhase === 'BLOCKED') {
        return false;
      }
      if (currentPhase === 'STARTED' || currentPhase === 'NRC_RECEIVED' || currentPhase === 'CARATULA_NRC') {
        return true;
      }
      if (currentPhase === 'PENDING' && (previousPhase === 'FINISHED' || previousPhase === 'BLOCKED')) {
        return true;
      }
      return false;
    }
  }
  

  saveFilters() {
    const filters = {
      selectedModels: this.selectedModels,
      selectedClients: this.selectedClients,
      selectedFase: this.selectedFase,
      selectedPresentationTypes: this.selectedPresentationTypes,
      selectedResponsible: this.selectedResponsible,
      selectedExecutor: this.selectedExecutor,
      selectedClientCodes: this.selectedClientCodes,
      selectedApplicationCodes: this.selectedApplicationCodes,
      selectedApplications: this.selectedApplications,
      sortField: this.sortField,
      sortOrder: this.sortOrder
    };
    localStorage.setItem('primeTableFilters', JSON.stringify(filters));
  }

  saveSortState() {
    const savedFilters = localStorage.getItem('primeTableFilters');
    let filters = savedFilters ? JSON.parse(savedFilters) : {};
    filters.sortField = this.sortField;
    filters.sortOrder = this.sortOrder;
    localStorage.setItem('primeTableFilters', JSON.stringify(filters));
  }

  applySavedSort() {
    if (this.sortField && this.filteredDeclarations && this.filteredDeclarations.length > 0) {
      this.isRestoringSort = true;
      // Crear un evento de ordenación simulado para aplicar la ordenación guardada
      const sortEvent: SortEvent = {
        field: this.sortField,
        order: this.sortOrder
      } as SortEvent;
      this.onSort(sortEvent);
      this.isRestoringSort = false;
    }
  }
  

  goToDetail(id: string, block?: boolean) {
    this.router.navigate(['/declarations/detail', id],  { state: { block: block } });
  }

  getHistoricCellStyle(key: string): Record<string, string> {
    const centered = ['client', 'observations', 'presentationType', 'dateFinish', 'hasNotifications', 'newDeclaration'].includes(key);
    return {
      cursor: 'pointer',
      ...(centered ? { 'text-align': 'center' } : {}),
    };
  }

  getHistoricColumnClass(key: string): string {
    switch (key) {
      case 'client':
        return 'historic-col-client-code';
      case 'clientName':
        return 'historic-col-client';
      case 'hasNotifications':
        return 'historic-compact-col historic-col-avisos';
      case 'observations':
        return 'historic-compact-col historic-col-comments';
      case 'newDeclaration':
        return 'historic-compact-col historic-col-related';
      case 'log':
        return 'historic-compact-col historic-col-log';
      default:
        return '';
    }
  }

  goToLog(id: string) {
    this.router.navigate(['/declarations/log', id]);
  }

  openAvisosModal(declaration: any, event: Event): void {
    event.stopPropagation();
    event.preventDefault();
    this.avisosModal.open(declaration);
  }

  onCheckboxChange(declaration: any) {
    if (declaration.isChecked) {
      this.selectedHash = declaration.hash;
      this.selectedDecsLength = this.selectedDecsLength + 1;
    } else {
      this.selectedDecsLength = this.selectedDecsLength - 1;
      if(this.selectedDecsLength === 0) {
        this.selectedHash = undefined;
      }
    }
    this.declarationChange.emit(declaration);
  }

  shouldDisableCheckbox(item: any) {
    if (!this.selectedHash) {
      return false;
    } else {
      return item.hash !== this.selectedHash;
    }
  }

  get canSelectAll(): boolean {
    if (!this.filteredDeclarations || this.filteredDeclarations.length === 0) {
      return false;
    }
    
    const validDeclarations = this.filteredDeclarations.filter(declaration => 
      declaration.type !== 'model-name' && !declaration.isFake
    );
    
    if (validDeclarations.length === 0) {
      return false;
    }
    
    // Verificar si todas tienen el mismo hash
    const firstHash = validDeclarations[0]?.hash;
    return validDeclarations.every(declaration => declaration.hash === firstHash);
  }

  get isAllSelected(): boolean {
    if (!this.filteredDeclarations || this.filteredDeclarations.length === 0) {
      return false;
    }
    
    const validDeclarations = this.filteredDeclarations.filter(declaration => 
      declaration.type !== 'model-name' && !declaration.isFake
    );
    
    if (validDeclarations.length === 0) {
      return false;
    }
    
    return validDeclarations.every(declaration => declaration.isChecked === true);
  }

  onSelectAllChange(isChecked: boolean) {
    const validDeclarations = this.filteredDeclarations.filter(declaration => 
      declaration.type !== 'model-name' && !declaration.isFake
    );
    
    if (validDeclarations.length === 0) {
      return;
    }
    
    validDeclarations.forEach(declaration => {
      const wasChecked = declaration.isChecked === true;
      declaration.isChecked = isChecked;
      
      if (isChecked && !wasChecked) {
        this.selectedHash = declaration.hash;
        this.selectedDecsLength = this.selectedDecsLength + 1;
        this.declarationChange.emit(declaration);
      } else if (!isChecked && wasChecked) {
        this.selectedDecsLength = this.selectedDecsLength - 1;
        this.declarationChange.emit(declaration);
      }
    });
    
    // Recontar: filas marcadas ocultas por un filtro también cuentan.
    this.selectedDecsLength = (this.declarations ?? []).filter((d: any) => d.isChecked === true).length;
    if (this.selectedDecsLength === 0) {
      this.selectedHash = undefined;
    }
  }
  onSort(event: SortEvent) {
    this.filteredDeclarations.forEach((declaration, index) => {
      if(declaration.type === 'model-name') {
        this.filteredDeclarations.splice(index, 1);
      }
    });
    

    const { field, order } = event;
    // Guardar estado de ordenación solo si no estamos restaurando
    this.sortField = field;
    this.sortOrder = order;
    if (!this.isRestoringSort) {
      this.saveSortState();
    }
    if(field === 'model') {
      this.filteredDeclarations.sort((a, b) => {
        
          const valueA = a[field]?.name;
          const valueB = b[field]?.name;

          const modelResult = valueA.localeCompare(valueB);

          if (modelResult !== 0) {
            return order * modelResult;
          }

        const nameA = a.client.name;
        const nameB = b.client.name;
        return nameA.localeCompare(nameB);
      });

      let modelName = '';
      const updatedDeclarations = [];
      // Siempre mostrar las filas de cabecera de modelo cuando está ordenado por modelo
      this.filteredDeclarations.forEach(declaration => {
        if (declaration.model.name !== modelName) {
          modelName = declaration.model.name;
          updatedDeclarations.push({ type: 'model-name', name: modelName });
        }
        updatedDeclarations.push(declaration);
      });
      this.filteredDeclarations.splice(0, this.filteredDeclarations.length, ...updatedDeclarations);

    } else if(field === 'clientName') {
      this.filteredDeclarations.sort((a, b) => {
          const valueA = a['client'].name;
          const valueB = b['client'].name;
          const result = valueA.localeCompare(valueB);
          return order * result;
      });
    } else if(field === 'responsible') {
      this.filteredDeclarations.sort((a, b) => {
          const valueA = a.responsible?.name || '';
          const valueB = b.responsible?.name || '';
          const result = valueA.localeCompare(valueB);
          return order * result;
      });
    } 
    else if(field === 'client') {
      this.filteredDeclarations.sort((a, b) => {
          const valueA = a['client'].code;
          const valueB = b['client'].code;  
          return (valueA > valueB ? 1 : -1) * order;
      });
    } else if(field === 'codeApplication') {
      this.filteredDeclarations.sort((a, b) => {
        const valueA = a.application?.code || 0;
        const valueB = b.application?.code || 0;
        return (valueA - valueB) * order;
      });
    } else {
      this.filteredDeclarations.sort((a, b) => {
        const valueA = a[field] || '';
        const valueB = b[field] || '';
        return (valueA > valueB ? 1 : -1) * order;
      });
    }

  }

  getDepartmentLabel(departmentValue: string): string {
    const department = this.departmentsTypes.find(dep => dep.value === departmentValue);
    return department ? department.label : '';
  }

  downloadDeclarations() {
    const declarations = this.filteredDeclarations.filter(declaration =>
      declaration.type !== 'model-name' && !declaration.isFake
    );
    this.filesService.createExcelFromDeclarationsTable(declarations);
  }

  get totalDeclarations(): number {
    if (!this.filteredDeclarations) return 0;
    return this.filteredDeclarations.filter(declaration => 
      declaration.type !== 'model-name' && !declaration.isFake
    ).length;
  }

  get selectedDeclarationsCount(): number {
    if (!this.filteredDeclarations) return 0;
    return this.filteredDeclarations.filter(declaration => 
      declaration.type !== 'model-name' && 
      !declaration.isFake && 
      declaration.isChecked === true
    ).length;
  }

  
}
